import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { describeRule, isGiftLine, loadSnapshot } from '@theme/gwp-engine';

/**
 * The cart page's free-gift message and progress bar (snippets/cart-products.liquid): "Belanja
 * Rp9.100 lagi untuk mendapatkan <gift> GRATIS", filling up as the cart approaches the promotion's
 * minimum, then "<gift> GRATIS sudah masuk keranjang" once the gift is really there and free.
 *
 * Everything shown is computed from the cart as it is right now (assets/gwp-engine.js), so it
 * follows every edit without any message being written here. It says nothing while there is nothing
 * true to say: no trigger product in the cart, no rule configured, or the minimum is met but the gift
 * has not landed yet (assets/buy-x-get-y-sync.js is adding it — the next paint says "granted").
 *
 * The cart section is re-rendered through morph on every edit, and the server markup of this element
 * is just an empty, hidden shell — so each morph wipes what was painted. Component's
 * `updatedCallback` is NOT a way to notice that: morph calls it on the node it parsed the new HTML
 * into, which is never upgraded, so it does not run for the live element (measured: zero calls across
 * a cart edit). A MutationObserver on the element itself is what catches the wipe; it repaints from the
 * last result in the same microtask checkpoint, before the browser draws, so nothing blinks. The
 * cart events that caused the morph have already scheduled a refresh, which then asks again.
 *
 * @typedef {object} GwpProgressRefs
 * @property {HTMLElement} message
 * @property {HTMLElement} track
 * @property {HTMLElement} bar
 *
 * @extends {Component<GwpProgressRefs>}
 */
export class GwpProgress extends Component {
  requiredRefs = ['message', 'track', 'bar'];

  #abortController = new AbortController();

  /** @type {import('@theme/gwp-engine').GiftStatus | null} */
  #status = null;

  /** @type {number | undefined} */
  #timer;

  /**
   * Repaints whenever a morph resets this element to the server's empty shell — and, when that
   * repaint had to restore the message, asks again: whatever the morph was rendering, the cart it
   * describes may no longer be the one this message was computed from.
   */
  #observer = new MutationObserver(() => {
    if (this.#paint()) this.#schedule();
  });

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['hidden', 'data-state'],
      childList: true,
      subtree: true,
      characterData: true,
    });
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#observer.disconnect();
    clearTimeout(this.#timer);
  }

  /**
   * ⚠️ The event is dispatched when an edit STARTS; the cart only changes when its promise settles
   * (assets/component-cart-items.js dispatches first, then waits for `/cart/change.js`). Asking the
   * cart straight away reads the cart as it was before the edit — on a slow connection the message
   * then stays that of the previous quantity for good, since nothing else prompts another look. So
   * wait for the edit to finish, and ask again after it.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    if (!event.promise) {
      this.#schedule();
      return;
    }

    event.promise.then(this.#schedule, this.#schedule);
  };

  /**
   * Debounced: one cart edit fires several events (the edit itself, then the gift correction and its
   * re-renders), and each only needs the last answer.
   */
  #schedule = () => {
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#refresh(), 300);
  };

  async #refresh() {
    const snapshot = await loadSnapshot();
    if (!snapshot || !this.isConnected) return;

    // The first rule with something to say; a store runs one gift promotion at a time in practice.
    this.#status = null;
    for (const rule of snapshot.rules) {
      const status = describeRule(rule, snapshot.cart.items, { unitPrice: highestEligibleUnitPrice(rule, snapshot.cart.items) });
      if (status.message) {
        this.#status = status;
        break;
      }
    }

    this.#paint();
  }

  /**
   * Writes the last result into the DOM — only where it differs, so the observer that calls this on
   * every mutation settles after one pass instead of feeding on its own writes.
   *
   * @returns {boolean} Whether anything was written.
   */
  #paint() {
    const message = this.querySelector('[ref="message"]');
    const track = this.querySelector('[ref="track"]');
    const bar = this.querySelector('[ref="bar"]');
    if (!(message instanceof HTMLElement) || !(track instanceof HTMLElement) || !(bar instanceof HTMLElement)) {
      return false;
    }

    const status = this.#status;

    if (!status?.message) {
      if (this.hidden) return false;

      this.hidden = true;
      return true;
    }

    let wrote = false;

    if (message.textContent !== status.message) {
      message.textContent = status.message;
      wrote = true;
    }

    const width = `${status.percent}%`;
    if (bar.style.width !== width) {
      bar.style.width = width;
      wrote = true;
    }

    const trackHidden = status.state === 'granted' || status.state === 'refused';
    if (track.hidden !== trackHidden) {
      track.hidden = trackHidden;
      wrote = true;
    }

    if (this.dataset.state !== status.state) {
      this.dataset.state = status.state;
      wrote = true;
    }

    if (this.hidden) {
      this.hidden = false;
      wrote = true;
    }

    return wrote;
  }
}

/**
 * The price of the most expensive eligible product in the cart — what "N more products" is counted in
 * on the cart page, which (unlike the product page) has no single product being looked at.
 *
 * @param {import('@theme/gwp-engine').GiftRule} rule
 * @param {any[]} items
 * @returns {number}
 */
function highestEligibleUnitPrice(rule, items) {
  return items
    .filter((item) => !isGiftLine(item) && (!rule.eligible_product_ids || rule.eligible_product_ids.includes(Number(item.product_id))))
    .reduce((highest, item) => Math.max(highest, Number(item.original_price) || 0), 0);
}

if (!customElements.get('gwp-progress')) {
  customElements.define('gwp-progress', GwpProgress);
}
